/**
 * Метки говорящих хранимого транскрипта и карта ролей категоризатора — одна
 * точка, где они сводятся (#1432).
 *
 * Карта ролей (`analysisMetadata.speakerRoles`) ключуется метками ASR
 * (`speaker_0`), а хранимый очищенный транскрипт (`variant = 'cleaned'`) после
 * категоризатора подписан ИМЕНАМИ («Оліна», «Клієнт», `remapSpeakers`). Шаг
 * критериев конвейера этим не страдает: диаризация к нему ещё несёт метки. А
 * всё, что читает сохранённый транскрипт — реплей, «Проверить на 20 звонках»,
 * харнесс замера, — роли по имени не находит (замер Э0 #868: роль у 3 записей
 * из 77). Раньше это обходилось в трёх местах по-разному; теперь — здесь.
 */

import type { SpeakerRole } from '../types/analysis-evidence';

/** Сегмент диаризации правильной формы — тот, что сверяется с корнем. */
export interface DiarizationSegmentShape {
  speaker: string;
  start: number;
  end: number;
  text: string;
}

/**
 * Годный сегмент. Один предикат на всех: реплей отбирает им сегменты из
 * хранилища (`asDiarization`), а перевод меток — пары для сверки; разойдись
 * они, один и тот же транскрипт переводился бы на одном пути и не на другом.
 */
export function isDiarizationSegment(item: unknown): item is DiarizationSegmentShape {
  return (
    !!item &&
    typeof item === 'object' &&
    typeof (item as DiarizationSegmentShape).speaker === 'string' &&
    typeof (item as DiarizationSegmentShape).start === 'number' &&
    typeof (item as DiarizationSegmentShape).end === 'number' &&
    typeof (item as DiarizationSegmentShape).text === 'string'
  );
}

/**
 * Какой ключ карты отвечает за метку — точный первым, без учёта регистра
 * вторым — или `undefined`, когда карта о метке не говорит ничего.
 *
 * Категоризатор пишет ключ так, как вернула модель, и при диаризации
 * `speaker_0` в карте встречается `SPEAKER_0` (прод, 30 дней: 4 карты из
 * ~6400, #1432). Регистровые двойники разрешаются ПЕРВЫМ найденным ключом —
 * так же, как их читают категоризатор, карточка анализа и привязка
 * key_moments: иначе один звонок получил бы в критериях одну атрибуцию, а в
 * анализе — другую (`/code-review` на #1484).
 *
 * Отдельно от «роль по метке», потому что ПИСАТЕЛЮ карты нужна разница,
 * которую читатель схлопывает: `unknown` бывает и «записано как unknown», и
 * «в карте нет». Писатель, принявший первое за второе, добавил бы ВТОРОЙ ключ
 * в другом регистре (`speaker_0: 'unknown'` рядом с `SPEAKER_0: 'agent'`), и
 * точное совпадение заслонило бы запасной путь. Собственное свойство — иначе
 * метка `constructor` нашла бы ключ в прототипе.
 */
export function findSpeakerRoleKey(
  label: string,
  speakerRoles: Readonly<Record<string, string>>
): string | undefined {
  // `hasOwnProperty.call`, а не `Object.hasOwn`: модуль собирают и воркспейсы
  // с библиотекой старше ES2022.
  if (Object.prototype.hasOwnProperty.call(speakerRoles, label)) return label;
  const lower = label.toLowerCase();
  return Object.keys(speakerRoles).find((candidate) => candidate.toLowerCase() === lower);
}

/** Роль метки по карте (`findSpeakerRoleKey`); `undefined` — карта не знает метку. */
export function speakerRoleFor(
  label: string,
  speakerRoles: Readonly<Record<string, string>>
): string | undefined {
  const key = findSpeakerRoleKey(label, speakerRoles);
  return key === undefined ? undefined : speakerRoles[key];
}

/**
 * Карта ролей, в которой каждая метка записана один раз (#2819).
 *
 * До #2117 писатель ключевал карту написанием модели, и сохранённые анализы
 * несут двойников (`SPEAKER_0` рядом с `speaker_0`: прод, 2 анализа из 18 121).
 * Анализ неизменяем (триггер `analyses_content_append_only_guard`), поэтому
 * данные не переписать, и реплей, берущий карту как есть, перенёс бы двойника в
 * новый анализ: читатели, сканирующие значения карты (ворота атрибуции,
 * `draft-finalization`), увидели бы роль, которую `resolveSpeakerRole` не
 * вернёт.
 *
 * Правила — те же, что у писателя (`mergeSpeakerClaims` в `categorize-step`):
 * двойники хранятся под меткой диаризации (ключ, равный ей, первым; иначе
 * метка, совпавшая без учёта регистра; иначе первый из встреченных ключей), доказанная роль побеждает 'unknown', две
 * противоположные доказанные роли дают 'unknown'. Карта без двойников
 * возвращается с теми же ключами в том же порядке.
 */
export function collapseSpeakerRoleTwins(
  speakerRoles: Readonly<Record<string, SpeakerRole>>,
  diarizationLabels: readonly string[]
): Record<string, SpeakerRole> {
  const groups = new Map<string, string[]>();
  for (const key of Object.keys(speakerRoles)) {
    const folded = key.toLowerCase();
    const group = groups.get(folded);
    if (group) group.push(key);
    else groups.set(folded, [key]);
  }
  if (groups.size === Object.keys(speakerRoles).length) return { ...speakerRoles };

  const labels = new Set(diarizationLabels);
  const collapsed: Array<[string, SpeakerRole]> = [];
  for (const [folded, keys] of groups) {
    const first = keys[0] as string;
    const kept =
      keys.length === 1
        ? first
        : (keys.find((key) => labels.has(key)) ??
          diarizationLabels.find((label) => label.toLowerCase() === folded) ??
          first);
    const proven = new Set(
      keys
        .map((key) => speakerRoles[key])
        .filter((role) => role === 'agent' || role === 'client')
    );
    collapsed.push([
      kept,
      proven.size === 1 ? ([...proven][0] as SpeakerRole) : 'unknown',
    ]);
  }
  return Object.fromEntries(collapsed);
}

/**
 * Сегменты хранимого транскрипта с метками его ASR-корня
 * (`source_transcript_id`, `variant = 'asr_source'`).
 *
 * Метка берётся ПО СЕГМЕНТУ и только при совпадении один в один — число
 * сегментов, границы, текст. Мягче нельзя: повторное распознавание хранится
 * производной версией, а `source_transcript_id` указывает на корень, и при
 * похожих границах позиционная пара раздала бы роли чужим репликам. По
 * сегменту, а не «имя → роль»: очистка может склеить две метки с разными
 * ролями в одно имя, а шаг конвейера видит каждую реплику со своей меткой.
 *
 * Метка корня ставится ВСЕГДА, даже если хранимое имя совпало с ключом карты.
 * На повторном прогоне по оплаченной расшифровке (`loadPaidTranscript`)
 * категоризатор получает сегменты с именами прошлого прогона, ключует карту
 * ими, а выход может переименовать говорящих иначе: хранимое «Клієнт» нового
 * прогона совпало бы с ключом «Клієнт» прошлого — то есть с ДРУГИМ
 * говорящим, — и реплики клиента ушли бы менеджеру. Метка корня на такой
 * карте роли не находит: роли нет, но и чужой роли нет (`/code-review` на
 * #1484). Неназванный говорящий сохраняет `speaker_N` и в хранимом, и в
 * корне — перевод его не меняет.
 *
 * Негодный сегмент (без `start`/`end`, не той формы) остаётся на своём месте
 * как есть: нарезка (`turnsFromTranscript`) его реплику принимает, а позиция
 * в массиве — это `sourceIndex`, по которому панель звонка возвращается от
 * номера реплики к моменту записи (#862). Сверяются только пары годных.
 *
 * Не совпало, ни одной годной пары или корня нет (анализ на самом
 * `asr_source`, чат) — `null`: вызывающий берёт хранимые сегменты как есть.
 * Тип элементов сохраняется: переведённый сегмент — тот же объект с другой
 * меткой.
 */
export function relabelFromRoot<T>(
  stored: readonly T[] | null | undefined,
  root: readonly unknown[] | null | undefined
): T[] | null {
  if (!stored || !root || stored.length === 0 || stored.length !== root.length) {
    return null;
  }
  let pairs = 0;
  const relabelled: T[] = [];
  for (let i = 0; i < stored.length; i++) {
    const segment = stored[i] as T;
    const rootSegment = root[i];
    if (!isDiarizationSegment(segment) || !isDiarizationSegment(rootSegment)) {
      relabelled.push(segment);
      continue;
    }
    if (
      segment.start !== rootSegment.start ||
      segment.end !== rootSegment.end ||
      segment.text !== rootSegment.text
    ) {
      return null;
    }
    pairs++;
    relabelled.push({ ...segment, speaker: rootSegment.speaker } as T);
  }
  return pairs > 0 ? relabelled : null;
}

/**
 * Карта «метка ASR → имя» из пары «хранимые сегменты (имена) — те же сегменты
 * с метками корня» (`relabelFromRoot`), по позиции. Её категоризатор держал в
 * контексте конвейера, а в хранилище она не попадает; без неё блок ролей
 * реплея называл метки, а строки очищенного текста — имена, и ни одна строка
 * не связывалась с ролью (Codex P2 на #1527). Метка с двумя разными именами
 * не попадает в карту: какое из них её, не сказать. Имя, совпавшее с меткой
 * (неназванный говорящий), — не запись карты.
 *
 * Одна функция на реплей конвейера и скрипт замера «голоса клиента»: замер
 * обязан собирать вход шага так же, как прод.
 */
export function speakerMapFromRelabel(
  stored: ReadonlyArray<{ speaker: string }>,
  relabelled: ReadonlyArray<{ speaker: string }>
): Record<string, string> | undefined {
  if (stored.length !== relabelled.length) return undefined;
  const map: Record<string, string> = {};
  const conflicted = new Set<string>();
  relabelled.forEach((segment, index) => {
    const label = segment.speaker;
    const name = stored[index]!.speaker;
    if (name === label || conflicted.has(label)) return;
    // `hasOwnProperty.call`, а не `Object.hasOwn`: модуль собирают и
    // воркспейсы с библиотекой старше ES2022.
    if (Object.prototype.hasOwnProperty.call(map, label) && map[label] !== name) {
      delete map[label];
      conflicted.add(label);
      return;
    }
    map[label] = name;
  });
  return Object.keys(map).length > 0 ? map : undefined;
}

/**
 * Роли ИМЁН хранимого транскрипта, выученные по его ASR-корню (#1476): то,
 * чем карту ролей (ключи — метки) дополняет читатель, который показывает
 * сегменты с именами, — ассистент. Реплей и шаги конвейера возвращают
 * сегментам сами метки (`relabelFromRoot`); ассистенту так нельзя: метка,
 * записанная в карте как `unknown` (категоризатор пишет её на каждого
 * неподтверждённого говорящего), или карта по именам прошлого прогона
 * спрятали бы имя от ступени «имя сотрудника», а окна цитат группируют
 * реплики по именам, которые видит модель.
 *
 * Сегменты сверяются с корнем один в один (`relabelFromRoot`); имя — с
 * точностью до регистра, по тому же правилу, что окна цитат: «Alex» и «alex»
 * — один говорящий. Имя получает:
 * - `agent` или `client` — когда КАЖДЫЙ его сегмент по метке даёт эту роль;
 * - `unknown` — когда среди его сегментов есть и сотрудник, и клиент
 *   (очистка свела две стороны в одно имя): ни одна нижняя ступень не
 *   должна назначить ему одну сторону;
 * - ничего — когда метки части сегментов роли не знают: имя остаётся
 *   нижним ступеням, как без корня.
 * Роль ставится каждому написанию имени. Имя, равное метке, тоже считается:
 * это ключ, по которому разрешаются хранимые сегменты.
 *
 * `null` — сегменты не сошлись с корнем.
 */
export function nameRolesFromRoot(
  stored: ReadonlyArray<{ speaker: string }>,
  root: readonly unknown[] | null | undefined,
  speakerRoles: Readonly<Record<string, string>>
): Record<string, SpeakerRole> | null {
  const relabelled = relabelFromRoot(stored, root);
  if (!relabelled) return null;
  const byName = new Map<
    string,
    { agent: boolean; client: boolean; other: boolean; spellings: Set<string> }
  >();
  relabelled.forEach((segment, index) => {
    const name = stored[index]!.speaker;
    const role = speakerRoleFor(segment.speaker, speakerRoles);
    const key = name.toLowerCase();
    let entry = byName.get(key);
    if (!entry) {
      entry = { agent: false, client: false, other: false, spellings: new Set() };
      byName.set(key, entry);
    }
    entry.spellings.add(name);
    if (role === 'agent') entry.agent = true;
    else if (role === 'client') entry.client = true;
    else entry.other = true;
  });
  const roles: Record<string, SpeakerRole> = {};
  for (const { agent, client, other, spellings } of byName.values()) {
    const role: SpeakerRole | null =
      agent && client ? 'unknown' : other ? null : agent ? 'agent' : 'client';
    if (role === null) continue;
    for (const name of spellings) roles[name] = role;
  }
  return roles;
}
